import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { FeaturesLayoutId, SectionLayoutConfig } from '@/constants/layoutTypes';

interface FeaturesSectionProps {
    features: Array<{ title: string; description: string }>;
    primaryColor: string;
    isEditing: boolean;
    onEdit: (section: string, field: string, value: string, index?: number) => void;
    sizeClasses: { heading: string; subheading: string; body: string; small: string };
    layout?: FeaturesLayoutId;
    layoutConfig?: SectionLayoutConfig;
    onTitleEdit?: (title: string) => void;
    onSubtitleEdit?: (subtitle: string) => void;
    onArrayAction?: (action: 'add' | 'delete', section: 'features' | 'faq', index?: number) => void;
}

// Default icons assigned per-index when user hasn't customised
const DEFAULT_ICONS = [
    'RocketLaunchIcon', 'ShieldCheckIcon', 'BoltIcon',
    'SparklesIcon', 'LightBulbIcon', 'ChartBarIcon',
    'GlobeAltIcon', 'CubeIcon', 'HeartIcon',
];

function getFeatureIcon(idx: number, layoutConfig?: SectionLayoutConfig): string {
    return layoutConfig?.featureIcons?.[idx] || DEFAULT_ICONS[idx % DEFAULT_ICONS.length];
}

// Shared delete button shown on hover over each feature card
function DeleteItemBtn({ onClick }: { onClick: () => void }) {
    return (
        <button
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            className="absolute -top-2 -right-2 z-20 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover/item:opacity-100 transition-opacity"
            title="Remove feature"
        >
            <Icon name="XMarkIcon" className="w-3.5 h-3.5" />
        </button>
    );
}

// Shared "Add Feature" button shown at the bottom of the section
function AddFeatureBtn({ onClick }: { onClick: () => void }) {
    return (
        <div className="flex justify-center mt-10">
            <button
                onClick={onClick}
                className="flex items-center gap-2 px-5 py-2.5 border-2 border-dashed border-blue-400 text-blue-600 rounded-xl hover:bg-blue-50 transition-colors text-sm font-medium"
            >
                <Icon name="PlusIcon" className="w-4 h-4" />
                Add Feature
            </button>
        </div>
    );
}

// ============================================================================
// LAYOUT 1: GRID CARDS (Improved)
// ============================================================================
function FeaturesGridCards({ features, primaryColor, isEditing, onEdit, sizeClasses, layoutConfig, onArrayAction }: Omit<FeaturesSectionProps, 'layout'>) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    return (
        <div className="grid @md:grid-cols-2 @3xl:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
                <div key={idx} className="relative group/item p-8 bg-gray-50 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-300">
                    {isEditing && onArrayAction && (
                        <DeleteItemBtn onClick={() => onArrayAction('delete', 'features', idx)} />
                    )}
                    <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                        style={{ backgroundColor: `${primaryColor}15` }}
                    >
                        <Icon name={getFeatureIcon(idx, layoutConfig)} className="w-6 h-6" style={{ color: primaryColor }} />
                    </div>
                    <h3
                        className={`${sizeClasses.subheading} font-bold text-gray-900 mb-3 ${editClass}`}
                        contentEditable={isEditing}
                        suppressContentEditableWarning
                        onBlur={(e) => onEdit('features', 'title', e.currentTarget.textContent || '', idx)}
                    >
                        {feature.title}
                    </h3>
                    <p
                        className={`${sizeClasses.body} text-gray-600 leading-relaxed ${editClass}`}
                        contentEditable={isEditing}
                        suppressContentEditableWarning
                        onBlur={(e) => onEdit('features', 'description', e.currentTarget.textContent || '', idx)}
                    >
                        {feature.description}
                    </p>
                </div>
            ))}
        </div>
    );
}

// ============================================================================
// LAYOUT 2: BENTO GRID (Improved)
// ============================================================================
function FeaturesBentoGrid({ features, primaryColor, isEditing, onEdit, sizeClasses, layoutConfig, onArrayAction }: Omit<FeaturesSectionProps, 'layout'>) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    return (
        <div className="grid @md:grid-cols-2 @3xl:grid-cols-4 gap-6 auto-rows-[minmax(200px,auto)]">
            {features.map((feature, idx) => {
                const isLarge = idx === 0 || idx === 3;
                const spanClass = isLarge ? '@3xl:col-span-2' : '';
                const bgClass = isLarge
                    ? 'bg-gradient-to-br from-gray-50 to-white'
                    : 'bg-gray-50';

                return (
                    <div
                        key={idx}
                        className={`relative group/item p-8 rounded-3xl border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col justify-between ${spanClass} ${bgClass}`}
                    >
                        {isEditing && onArrayAction && (
                            <DeleteItemBtn onClick={() => onArrayAction('delete', 'features', idx)} />
                        )}
                        <div>
                            <div
                                className="w-11 h-11 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                                style={{ backgroundColor: isLarge ? primaryColor : `${primaryColor}15` }}
                            >
                                <Icon
                                    name={getFeatureIcon(idx, layoutConfig)}
                                    className="w-5 h-5"
                                    style={{ color: isLarge ? 'white' : primaryColor }}
                                />
                            </div>
                            <h3
                                className={`${isLarge ? 'text-xl @3xl:text-2xl' : sizeClasses.subheading} font-bold text-gray-900 mb-3 ${editClass}`}
                                contentEditable={isEditing}
                                suppressContentEditableWarning
                                onBlur={(e) => onEdit('features', 'title', e.currentTarget.textContent || '', idx)}
                            >
                                {feature.title}
                            </h3>
                        </div>
                        <p
                            className={`${sizeClasses.body} text-gray-500 leading-relaxed mt-4 ${editClass}`}
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            onBlur={(e) => onEdit('features', 'description', e.currentTarget.textContent || '', idx)}
                        >
                            {feature.description}
                        </p>
                    </div>
                );
            })}
        </div>
    );
}

// ============================================================================
// LAYOUT 3: ALTERNATING ROWS (Improved)
// ============================================================================
function FeaturesAlternating({ features, primaryColor, isEditing, onEdit, sizeClasses, layoutConfig, onArrayAction }: Omit<FeaturesSectionProps, 'layout'>) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    return (
        <div className="space-y-16 @3xl:space-y-24">
            {features.map((feature, idx) => {
                const isEven = idx % 2 === 0;
                return (
                    <div key={idx} className={`relative group/item flex flex-col @3xl:flex-row items-center gap-8 @3xl:gap-16 ${!isEven ? '@3xl:flex-row-reverse' : ''}`}>
                        {isEditing && onArrayAction && (
                            <DeleteItemBtn onClick={() => onArrayAction('delete', 'features', idx)} />
                        )}
                        {/* Visual Side */}
                        <div className="flex-1 w-full">
                            <div
                                className="aspect-[4/3] rounded-2xl w-full flex items-center justify-center overflow-hidden relative group"
                                style={{ background: `linear-gradient(135deg, ${primaryColor}10 0%, ${primaryColor}20 100%)` }}
                            >
                                <div
                                    className="w-24 h-24 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-500"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    <Icon name={getFeatureIcon(idx, layoutConfig)} className="w-10 h-10 text-white" />
                                </div>
                                <div className="absolute top-4 right-4 w-16 h-1 bg-white/50 rounded-full" />
                                <div className="absolute bottom-4 left-4 w-8 h-1 bg-white/50 rounded-full" />
                            </div>
                        </div>

                        {/* Content Side */}
                        <div className="flex-1 text-center @3xl:text-left">
                            <div className="inline-flex items-center justify-center @3xl:justify-start gap-2 mb-4 font-semibold opacity-80" style={{ color: primaryColor }}>
                                <span className="text-sm">0{idx + 1}</span>
                                <div className="w-8 h-[1px] bg-current" />
                            </div>
                            <h3
                                className={`${sizeClasses.heading} !text-2xl @3xl:!text-3xl font-bold text-gray-900 mb-4 ${editClass}`}
                                contentEditable={isEditing}
                                suppressContentEditableWarning
                                onBlur={(e) => onEdit('features', 'title', e.currentTarget.textContent || '', idx)}
                            >
                                {feature.title}
                            </h3>
                            <p
                                className={`${sizeClasses.body} text-gray-600 leading-relaxed max-w-lg mx-auto @3xl:mx-0 ${editClass}`}
                                contentEditable={isEditing}
                                suppressContentEditableWarning
                                onBlur={(e) => onEdit('features', 'description', e.currentTarget.textContent || '', idx)}
                            >
                                {feature.description}
                            </p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ============================================================================
// LAYOUT 4: MINIMAL LIST (Improved)
// ============================================================================
function FeaturesMinimalList({ features, primaryColor, isEditing, onEdit, sizeClasses, layoutConfig, onArrayAction }: Omit<FeaturesSectionProps, 'layout'>) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    return (
        <div className="max-w-4xl mx-auto space-y-4">
            {features.map((feature, idx) => (
                <div key={idx} className="relative group/item flex flex-col @md:flex-row gap-5 p-6 rounded-2xl hover:bg-gray-50 transition-all duration-300 border border-transparent hover:border-gray-100">
                    {isEditing && onArrayAction && (
                        <DeleteItemBtn onClick={() => onArrayAction('delete', 'features', idx)} />
                    )}
                    <div className="flex-shrink-0">
                        <div
                            className="w-14 h-14 rounded-full flex items-center justify-center shadow-sm border border-gray-100 bg-white group-hover:shadow-md transition-shadow duration-300"
                        >
                            <Icon name={getFeatureIcon(idx, layoutConfig)} className="w-6 h-6" style={{ color: primaryColor }} />
                        </div>
                    </div>
                    <div className="flex-1">
                        <h3
                            className={`${sizeClasses.subheading} font-bold text-gray-900 mb-2 ${editClass}`}
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            onBlur={(e) => onEdit('features', 'title', e.currentTarget.textContent || '', idx)}
                        >
                            {feature.title}
                        </h3>
                        <p
                            className={`${sizeClasses.body} text-gray-600 leading-relaxed ${editClass}`}
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            onBlur={(e) => onEdit('features', 'description', e.currentTarget.textContent || '', idx)}
                        >
                            {feature.description}
                        </p>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ============================================================================
// LAYOUT 5: ICON LEFT (New)
// ============================================================================
function FeaturesIconLeft({ features, primaryColor, isEditing, onEdit, sizeClasses, layoutConfig, onArrayAction }: Omit<FeaturesSectionProps, 'layout'>) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    return (
        <div className="grid @md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {features.map((feature, idx) => (
                <div
                    key={idx}
                    className="relative group/item flex gap-5 p-6 rounded-2xl bg-white border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-300"
                >
                    {isEditing && onArrayAction && (
                        <DeleteItemBtn onClick={() => onArrayAction('delete', 'features', idx)} />
                    )}
                    {/* Icon */}
                    <div className="flex-shrink-0">
                        <div
                            className="w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                            style={{ backgroundColor: `${primaryColor}12` }}
                        >
                            <Icon name={getFeatureIcon(idx, layoutConfig)} className="w-7 h-7" style={{ color: primaryColor }} />
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                        <h3
                            className={`text-lg font-bold text-gray-900 mb-1.5 ${editClass}`}
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            onBlur={(e) => onEdit('features', 'title', e.currentTarget.textContent || '', idx)}
                        >
                            {feature.title}
                        </h3>
                        <p
                            className={`${sizeClasses.body} text-gray-500 leading-relaxed ${editClass}`}
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            onBlur={(e) => onEdit('features', 'description', e.currentTarget.textContent || '', idx)}
                        >
                            {feature.description}
                        </p>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ============================================================================
// LAYOUT 6: CENTERED ICONS (New)
// ============================================================================
function FeaturesCenteredIcons({ features, primaryColor, isEditing, onEdit, sizeClasses, layoutConfig, onArrayAction }: Omit<FeaturesSectionProps, 'layout'>) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    return (
        <div className="grid @md:grid-cols-2 @3xl:grid-cols-3 gap-10">
            {features.map((feature, idx) => (
                <div key={idx} className="relative group/item text-center">
                    {isEditing && onArrayAction && (
                        <DeleteItemBtn onClick={() => onArrayAction('delete', 'features', idx)} />
                    )}
                    {/* Large centered icon */}
                    <div className="flex justify-center mb-6">
                        <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:shadow-lg transition-all duration-300 relative"
                            style={{ backgroundColor: primaryColor }}
                        >
                            {/* Subtle glow */}
                            <div
                                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-300"
                                style={{ backgroundColor: primaryColor }}
                            />
                            <Icon name={getFeatureIcon(idx, layoutConfig)} className="w-8 h-8 text-white relative z-10" />
                        </div>
                    </div>

                    <h3
                        className={`${sizeClasses.subheading} font-bold text-gray-900 mb-3 ${editClass}`}
                        contentEditable={isEditing}
                        suppressContentEditableWarning
                        onBlur={(e) => onEdit('features', 'title', e.currentTarget.textContent || '', idx)}
                    >
                        {feature.title}
                    </h3>
                    <p
                        className={`${sizeClasses.body} text-gray-500 leading-relaxed max-w-sm mx-auto ${editClass}`}
                        contentEditable={isEditing}
                        suppressContentEditableWarning
                        onBlur={(e) => onEdit('features', 'description', e.currentTarget.textContent || '', idx)}
                    >
                        {feature.description}
                    </p>
                </div>
            ))}
        </div>
    );
}

// ============================================================================
// MAIN EXPORT
// ============================================================================
export function FeaturesSection({
    features,
    primaryColor,
    isEditing,
    onEdit,
    sizeClasses,
    layout = 'grid-cards',
    layoutConfig,
    onTitleEdit,
    onSubtitleEdit,
    onArrayAction,
}: FeaturesSectionProps) {
    const commonProps = { features, primaryColor, isEditing, onEdit, sizeClasses, layoutConfig, onArrayAction };

    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    const sectionTitle = layoutConfig?.featuresTitle || 'Why Choose Us';
    const sectionSubtitle = layoutConfig?.featuresSubtitle || 'Everything you need to succeed, all in one place.';

    const renderLayout = () => {
        switch (layout) {
            case 'bento-grid':
                return <FeaturesBentoGrid {...commonProps} />;
            case 'alternating':
                return <FeaturesAlternating {...commonProps} />;
            case 'minimal-list':
                return <FeaturesMinimalList {...commonProps} />;
            case 'icon-left':
                return <FeaturesIconLeft {...commonProps} />;
            case 'centered-icons':
                return <FeaturesCenteredIcons {...commonProps} />;
            case 'grid-cards':
            default:
                return <FeaturesGridCards {...commonProps} />;
        }
    };

    return (
        <section id="features" className="py-20 px-6 bg-white @container">
            <div className="max-w-7xl mx-auto">
                {/* Section Header */}
                <div className="text-center mb-16 max-w-3xl mx-auto">
                    <h2
                        className={`${sizeClasses.heading} font-bold text-gray-900 mb-4 ${editClass}`}
                        contentEditable={isEditing}
                        suppressContentEditableWarning
                        onBlur={(e) => onTitleEdit?.(e.currentTarget.textContent || '')}
                    >
                        {sectionTitle}
                    </h2>
                    <p
                        className={`${sizeClasses.body} text-gray-500 leading-relaxed ${editClass}`}
                        contentEditable={isEditing}
                        suppressContentEditableWarning
                        onBlur={(e) => onSubtitleEdit?.(e.currentTarget.textContent || '')}
                    >
                        {sectionSubtitle}
                    </p>
                </div>

                {renderLayout()}

                {/* Add Feature button — editor only */}
                {isEditing && onArrayAction && (
                    <AddFeatureBtn onClick={() => onArrayAction('add', 'features')} />
                )}
            </div>
        </section>
    );
}
